import { SVGProps, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'next-i18next/pages';

import { Tooltip } from 'antd';

import { TranslationNamespace } from '@/i18n';

import {
	buildChartData,
	type ChartBar,
	type ChartScenario,
	type CostGroup,
	formatRub,
	formatShortMoney,
} from '../../lib';

import styles from '@/components/TariffCalculatorNew/TariffCalculator.module.scss';

const COLORS = {
	blue: '#126783',
	orange: '#f2762e',
	grid: '#d7dee8',
	ink: '#33445d',
} as const;

const MIN_BAR_SIZE = 4;

const getBarSize = (value: number, availableSize: number, max: number) =>
	Math.max(MIN_BAR_SIZE, (availableSize * value) / max);

function Bar({
	bar,
	series,
	...rectProps
}: {
	bar: ChartBar;
	series: 'current' | 'saving';
} & SVGProps<SVGRectElement>) {
	const value = series === 'current' ? bar.current : bar.saving;
	const title = `${bar.label}: ${formatRub(value)}`;

	return (
		<Tooltip title={title} mouseEnterDelay={0.1}>
			<rect {...rectProps} tabIndex={0} aria-label={title} />
		</Tooltip>
	);
}

interface CostChartProps {
	groups: CostGroup[];
	scenario: ChartScenario;
}

export function CostChart({ groups, scenario }: CostChartProps) {
	const { t } = useTranslation(TranslationNamespace.Calculator);
	const boxRef = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState(800);

	useEffect(() => {
		const el = boxRef.current;
		if (!el) return;

		const update = () => setWidth(Math.max(300, el.clientWidth));
		update();

		const observer = new ResizeObserver(update);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const data = buildChartData(groups, scenario);
	const compact = width < 650;
	const max = Math.max(1, ...data.flatMap((bar) => [bar.current, bar.saving])) * 1.08;
	const scenarioLabel = t(scenario === 'cons' ? 'chart.conservative' : 'chart.positive');
	const ariaLabel = t('chart.aria', { scenario: scenarioLabel });

	return (
		<div className={styles.chartBox} ref={boxRef}>
			{compact ? (
				<CompactChart data={data} max={max} ariaLabel={ariaLabel} />
			) : (
				<DesktopChart data={data} max={max} ariaLabel={ariaLabel} />
			)}
		</div>
	);
}

function CompactChart({
	data,
	max,
	ariaLabel,
}: {
	data: ChartBar[];
	max: number;
	ariaLabel: string;
}) {
	const chartWidth = 360;
	const chartHeight = 560;
	const left = 116;
	const right = 8;
	const top = 34;
	const bottom = 42;
	const plotWidth = chartWidth - left - right;
	const rowHeight = (chartHeight - top - bottom) / data.length;

	return (
		<svg role="img" aria-label={ariaLabel} viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
			{Array.from({ length: 5 }, (_, index) => {
				const x = left + (plotWidth * index) / 4;
				return (
					<g key={index}>
						<line
							x1={x}
							y1={top}
							x2={x}
							y2={chartHeight - bottom}
							stroke={COLORS.grid}
							strokeDasharray="5 5"
						/>
						<text x={x} y={chartHeight - 14} textAnchor="middle" fill={COLORS.ink} fontSize="9">
							{formatShortMoney((max * index) / 4)}
						</text>
					</g>
				);
			})}
			{data.map((bar, index) => {
				const y = top + index * rowHeight + 8;
				const currentWidth = getBarSize(bar.current, plotWidth, max);
				const savingWidth = getBarSize(bar.saving, plotWidth, max);
				return (
					<g key={bar.label}>
						<text x={left - 8} y={y + 11} textAnchor="end" fill={COLORS.ink} fontSize="10">
							<tspan x={left - 8}>{bar.lines[0]}</tspan>
							<tspan x={left - 8} dy="13">
								{bar.lines[1]}
							</tspan>
						</text>
						<Bar
							bar={bar}
							series="current"
							x={left}
							y={y}
							width={currentWidth}
							height={17}
							rx={3}
							fill={COLORS.blue}
						/>
						<Bar
							bar={bar}
							series="saving"
							x={left}
							y={y + 23}
							width={savingWidth}
							height={17}
							rx={3}
							fill={COLORS.orange}
						/>
					</g>
				);
			})}
		</svg>
	);
}

function DesktopChart({
	data,
	max,
	ariaLabel,
}: {
	data: ChartBar[];
	max: number;
	ariaLabel: string;
}) {
	const chartWidth = 960;
	const chartHeight = 470;
	const left = 70;
	const right = 24;
	const top = 28;
	const bottom = 112;
	const plotWidth = chartWidth - left - right;
	const plotHeight = chartHeight - top - bottom;
	const groupWidth = plotWidth / data.length;
	const barWidth = Math.min(35, groupWidth * 0.28);

	return (
		<svg role="img" aria-label={ariaLabel} viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
			{Array.from({ length: 5 }, (_, index) => {
				const y = top + plotHeight - (plotHeight * index) / 4;
				return (
					<g key={index}>
						<line
							x1={left}
							y1={y}
							x2={chartWidth - right}
							y2={y}
							stroke={COLORS.grid}
							strokeDasharray="5 5"
						/>
						<text x={left - 10} y={y + 5} textAnchor="end" fill={COLORS.ink} fontSize="13">
							{formatShortMoney((max * index) / 4)}
						</text>
					</g>
				);
			})}
			{data.map((bar, index) => {
				const centerX = left + groupWidth * (index + 0.5);
				const currentHeight = getBarSize(bar.current, plotHeight, max);
				const savingHeight = getBarSize(bar.saving, plotHeight, max);
				return (
					<g key={bar.label}>
						<Bar
							bar={bar}
							series="current"
							x={centerX - barWidth - 3}
							y={top + plotHeight - currentHeight}
							width={barWidth}
							height={currentHeight}
							rx={3}
							fill={COLORS.blue}
						/>
						<Bar
							bar={bar}
							series="saving"
							x={centerX + 3}
							y={top + plotHeight - savingHeight}
							width={barWidth}
							height={savingHeight}
							rx={3}
							fill={COLORS.orange}
						/>
						<text
							x={centerX}
							y={top + plotHeight + 25}
							textAnchor="middle"
							fill={COLORS.ink}
							fontSize="13"
						>
							<tspan x={centerX}>{bar.lines[0]}</tspan>
							<tspan x={centerX} dy="17">
								{bar.lines[1]}
							</tspan>
						</text>
					</g>
				);
			})}
		</svg>
	);
}
